import {
  Box,
  Chip,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomInput } from "global/components";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { isTruthy, openErrorNotification } from "helpers/methods";
import { pageFilters, UserFields } from "models/interfaces";
import { useEffect, useState } from "react";
import settingsStyles from "../Settings.styles";
import strings from "global/constants/StringConstants";
import { AppModal } from "global/components/AppModal";
import { AddTeamFields } from "types/team";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import { hasAccessTo } from "layouts/Config";
import { handleAddUpdateOnSubmit, teamFieldIds } from "./TeamHandler";
import { SeatBlockReason } from "helpers/perSeatHelpers";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(shift: string, personName: readonly string[]) {
  return {
    fontWeight: personName.indexOf(shift) === -1 ? 400 : 500,
  };
}

/**
 * Same label chrome as `CustomInput`'s — the Roles field is a bare MUI
 * `Select`, not `CustomInput`, so it doesn't get this for free and used to
 * run 14px/marginLeft instead of the 11.5px every other field in the dialog
 * uses.
 */
const roleLabelSx = (theme: Theme) => ({
  textAlign: "left" as const,
  ml: 0,
  color: theme.palette.formField.label,
  fontSize: formFieldMetrics.labelFontSize,
  fontWeight: formFieldMetrics.labelFontWeight,
  lineHeight: "normal",
  "& .MuiFormLabel-asterisk": {
    color: theme.palette.formField.required,
    ml: formFieldMetrics.asteriskGap,
  },
});

/** Same box chrome as `CustomInput`'s (`CustomInput.styles.ts`'s `textField`). */
const roleSelectSx = (theme: Theme) => ({
  height: formFieldMetrics.height,
  borderRadius: formFieldMetrics.radius,
  bgcolor: theme.palette.formField.bg,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.formField.border,
    borderWidth: formFieldMetrics.borderWidth,
  },
  "&:hover": {
    bgcolor: theme.palette.formField.bg,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.formField.border,
    },
  },
  "&.Mui-focused": {
    bgcolor: theme.palette.formField.bg,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.formField.focusBorder,
      borderWidth: formFieldMetrics.borderWidth,
    },
  },
  "&.Mui-disabled": {
    bgcolor: theme.palette.formField.disabledBg,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.formField.disabledBorder,
    },
  },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.error.main,
  },
  "& .MuiSelect-select": {
    height: "100%",
    boxSizing: "border-box" as const,
    display: "flex",
    alignItems: "center",
    p: `0 ${formFieldMetrics.paddingX}`,
    fontSize: formFieldMetrics.fontSize,
    fontWeight: formFieldMetrics.fontWeight,
    color: theme.palette.formField.text,
  },
});

interface CustomProps {
  user: UserFields;
  visible: boolean;
  handleDialogClose: Function;
  allRoles: { id: number; name: string }[];
  isEdit: boolean;
  setInviteUser: Function;
  convertDataToTableFormat: any;
  setRoles: Function;
  setCount: Function;
  setTeam: Function;
  teamFilters: pageFilters;
  /** Refetches the seat meter once the invite or edit has gone through. */
  onSeatsChanged?: () => Promise<void> | void;
  /**
   * Routes a `406` (no free seat) to the shared seat prompt instead of a toast.
   * Inviting someone never costs anything — it fills a seat the account has
   * already paid for — so there is no cost step here.
   */
  onSeatBlocked?: (reason: SeatBlockReason, message?: string) => void;
  /**
   * The Team page's own loading flag — its table skeleton is driven by this,
   * not by this dialog's local `isLoading`, which stops mattering once the
   * dialog closes and the refetch is still running.
   */
  setTableLoading?: Function;
}

const InviteUser = (props: CustomProps) => {
  const theme = useTheme();
  const classes = settingsStyles(theme);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const initialState = {
    account: "",
    email: "",
    name: "",
    firstName: "",
    lastName: "",
    contactNo: "",
    roleIds: [],
    id: 0,
  };
  const [user, setUser] = useState<AddTeamFields>(initialState);
  const [errors, setErrors] = useState<{ [key: string]: any }>({
    email: "",
    firstName: "",
    lastName: "",
    contactNo: "",
    roleIds: [],
  });
  const [selectedRolesChips, setSelectedRolesChips] = useState("");
  const userAccess = hasAccessTo(strings.resources.app, strings.addPermission);

  useEffect(() => {
    if (props.isEdit && props.user) {
      setUser({
        email: props.user.email || "",
        name: props.user.name || "",
        firstName: props.user.firstName || "",
        lastName: props.user.lastName || "",
        contactNo: props.user.contactNo || "",
        roleIds: props.user.roleIds || [],
        id: props.user.id || 0,
      });
    } else {
      setUser(initialState);
    }
  }, [props.isEdit, props.user, props.visible]);

  const handleOnChangeInputField = (e: any) => {
    const { value } = e.target;
    const spaceSeparatedValue = value.replace(/\s+/g, "");
    setUser({ ...user, [e.target.name]: spaceSeparatedValue });
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  useEffect(() => {
    if (isTruthy(user.contactNo)) {
      if (!isNumberOrCharacter(user.contactNo)) {
        setErrors({ ...errors, contactNo: "Only Numbers are allowed!" });
      } else {
        setErrors({ ...errors, contactNo: "" });
      }
    } else {
      setErrors({ ...errors, contactNo: "" });
    }
  }, [user.contactNo]);

  function isNumberOrCharacter(value: string) {
    const regex = /^[\d()+\- ]+$/;
    return regex.test(value);
  }

  const handleInputBlur = (e: any) => {
    setUser({ ...user, [e.target.name]: e.target.value.trim() });
  };

  const handleDialogClose = () => {
    setSelectedRolesChips("");
    setUser(initialState);
    setErrors({
      email: "",
      name: "",
      firstName: "",
      lastName: "",
      allowedEmailCount: "",
      assignBy: "",
      roleIds: [],
    });
    props.handleDialogClose();
  };

  const handleRoleChange = (event: any) => {
    const parsedRole = JSON.parse(event?.target.value);
    const alreadyExists = user.roleIds.find(
      (role: { id: number; name: string }) => role.id === parsedRole.id,
    );
    if (isTruthy(alreadyExists)) {
      openErrorNotification("You have already selected the role.");
    } else {
      setSelectedRolesChips(parsedRole.id);
      setUser({ ...user, roleIds: [...user.roleIds, parsedRole] });
    }
  };

  const handleDeleteSelectedRoles = (roleId: number) => {
    const rolesArray = user.roleIds.filter((role: any) => role.id !== roleId);
    setUser({
      ...user,
      roleIds: rolesArray,
    });
  };

  const getFields = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <CustomInput
              required
              label="First Name"
              id={teamFieldIds.firstName}
              placeHolder="Enter first name"
              type="text"
              name="firstName"
              onChange={handleOnChangeInputField}
              onBlur={handleInputBlur}
              value={user.firstName}
              propsToInputElement={{ minLength: 1, maxLength: 30 }}
              disabled={!userAccess || isLoading}
              error={errors.firstName}
              helperText={errors.firstName}
              sx={{
                        "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
                          outline: "2px solid",
                          outlineColor: "formField.focusBorder",
                          outlineOffset: "2px",
                        },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <CustomInput
              required
              id={teamFieldIds.lastName}
              label="Last Name"
              placeHolder="Enter last name"
              type="text"
              name="lastName"
              onChange={handleOnChangeInputField}
              onBlur={handleInputBlur}
              value={user.lastName}
              propsToInputElement={{ minLength: 1, maxLength: 30 }}
              error={errors.lastName}
              helperText={errors.lastName}
              sx={{
                        "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
                          outline: "2px solid",
                          outlineColor: "formField.focusBorder",
                          outlineOffset: "2px",
                        },
              }}
              disabled={!userAccess || isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <CustomContactNumberInput
              label="Phone"
              required
              id={teamFieldIds.contactNo}
              name="contactNo"
              value={user.contactNo || ""}
              placeHolder="Phone"
              onChange={(value) => {
                setErrors({ ...errors, contactNo: "" });
                setUser({ ...user, contactNo: value });
              }}
              error={errors.contactNo}
              helperText={errors.contactNo}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
                  outline: "2px solid",
                  outlineColor: "formField.focusBorder",
                  outlineOffset: "2px",
                },
              }}
              disabled={!userAccess || isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <CustomInput
              required
              id={teamFieldIds.email}
              label="Email"
              placeHolder="user@example.com"
              type="text"
              name="email"
              onChange={handleOnChangeInputField}
              onBlur={handleInputBlur}
              value={user.email}
              disabled={!userAccess || props.isEdit || isLoading}
              propsToInputElement={{ minLength: 1, maxLength: 255 }}
              error={errors.email}
              helperText={errors.email}
              sx={{
                        "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
                          outline: "2px solid",
                          outlineColor: "formField.focusBorder",
                          outlineOffset: "2px",
                        },
              }}
            />
          </Grid>
          {/* Full width, not a half column: a role picker needs room for its
              selected-role chips, and pairing it against a blank half column
              read as an unfinished layout. */}
          <Grid item xs={12}>
            <Stack direction="column">
              <InputLabel id="invite-roles-label" required sx={roleLabelSx(theme)}>
                Roles
              </InputLabel>
              <Box mt={formFieldMetrics.labelGap}>
                <Select
                  required
                  sx={roleSelectSx(theme)}
                  labelId="invite-roles-label"
                  SelectDisplayProps={{
                    id: teamFieldIds.roleIds,
                    "aria-invalid":
                      (!isTruthy(user.roleIds?.[0]) &&
                        errors.roleIds.length !== 0) ||
                      undefined,
                  }}
                  name="roleIds"
                  aria-describedby={
                    !isTruthy(user.roleIds?.[0]) && errors.roleIds.length !== 0
                      ? "invite-roles-error"
                      : undefined
                  }
                  value={selectedRolesChips}
                  onChange={handleRoleChange}
                  disabled={!userAccess || isLoading}
                  renderValue={(selected) => {
                    if (selected.length >= 0) {
                      return (
                        <Typography
                          textAlign="left"
                          sx={{
                            fontSize: formFieldMetrics.fontSize,
                            fontWeight: formFieldMetrics.fontWeight,
                            color: theme.palette.formField.placeholder,
                          }}
                        >
                          Select Roles
                        </Typography>
                      );
                    }
                  }}
                  displayEmpty
                  MenuProps={MenuProps}
                  error={
                    !isTruthy(user.roleIds?.[0]) && errors.roleIds.length !== 0
                  }
                >
                  {props.allRoles?.map((role: any) => (
                    <MenuItem
                      key={role.name}
                      value={JSON.stringify({ id: role.id, name: role.name })}
                      style={getStyles(role.name, user.roleIds)}
                    >
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                {!isTruthy(user.roleIds?.[0]) && (
                  <FormHelperText
                    id="invite-roles-error"
                    error
                    sx={{ ml: 0, mt: "4px", fontSize: formFieldMetrics.helperFontSize }}
                  >
                    {errors.roleIds}
                  </FormHelperText>
                )}
                {isTruthy(user.roleIds) && (
                  <Box sx={{ width: "100%", textAlign: "left" }} mt={1}>
                    {user.roleIds.map(
                      (role: { id: number; name: string }, index: number) => (
                        <Chip
                          disabled={isLoading}
                          key={index}
                          label={role.name}
                          sx={classes.selectedChip}
                          onDelete={() => handleDeleteSelectedRoles(role.id)}
                        />
                      ),
                    )}
                  </Box>
                )}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </>
    );
  };

  const handleSubmit = () =>
    handleAddUpdateOnSubmit(
      user,
      props.isEdit,
      errors,
      setErrors,
      setIsLoading,
      handleDialogClose,
      props.setInviteUser,
      props.convertDataToTableFormat,
      props.setRoles,
      props.setCount,
      props.setTeam,
      props.teamFilters,
      props.onSeatBlocked,
      props.onSeatsChanged,
      props.setTableLoading,
    );

  return (
    <AppModal
      open={props.visible}
      onClose={() => handleDialogClose()}
      title={props.isEdit ? "Edit User" : "Send Invite"}
      // A multi-field form — the design system's L width.
      size="lg"
      busy={isLoading}
      primaryLabel={props.isEdit ? "Update" : "Send"}
      onPrimary={handleSubmit}
      primaryLoading={isLoading}
      cancelLabel="Cancel"
      onCancel={() => handleDialogClose()}
    >
      {getFields()}
    </AppModal>
  );
};

export default InviteUser;
